import { getCurrentScope, onScopeDispose, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { Router } from 'vue-router'
import { getGlobalT } from '@/i18n'

/**
 * One editing surface's unsaved work, registered while its editor lives.
 *
 * `flush` persists the draft and resolves `true` when the surface is clean
 * afterwards. Surfaces whose draft cannot be persisted automatically (e.g.
 * answers of an already-reviewed form) omit it — their draft can only be
 * kept or knowingly discarded.
 */
export interface DraftRegistration {
  /** Stable identity, e.g. `note:dossierNote:<appointmentId>` or `form-submission:<id>`. */
  key: string
  isDirty: () => boolean
  flush?: () => Promise<boolean>
}

/**
 * Central registry of in-progress edits (#3297).
 *
 * The afspraakpagina lost verslaglegging in three silent ways: navigating away
 * with a dirty note, collapsing a form with unsaved answers, and status actions
 * refreshing the appointment under an open draft. Every editing surface now
 * registers here, and every transition that would drop work goes through this
 * one mechanism — notes and forms share it rather than each growing their own
 * (the pattern is generalized from LetterTemplateEditorView's bespoke guard).
 *
 * Module-level state: the registry spans routing boundaries by design, exactly
 * like the drafts it protects.
 */
const registrations = new Map<string, DraftRegistration>()

/** Register a surface's draft; returns the unregister function (also bound to scope disposal). */
export function registerDraft(registration: DraftRegistration): () => void {
  registrations.set(registration.key, registration)
  const unregister = () => {
    // Only remove our own entry: a remount may have re-registered the key.
    if (registrations.get(registration.key) === registration) {
      registrations.delete(registration.key)
    }
  }
  if (getCurrentScope()) {
    onScopeDispose(unregister)
  }
  return unregister
}

/**
 * Register an entity detail form's draft, re-keying when the entity changes and
 * unregistering on scope disposal. Nothing is registered while the id is absent,
 * so a view may call this before its entity has loaded.
 *
 * The key is `detail-form:<entity>:<id>` — stable across re-renders and unique
 * per edited entity, so two open detail forms cannot shadow each other.
 *
 * No `flush` by design: a detail form is validated on save, so its draft cannot
 * be persisted unattended. The user is asked to keep or knowingly discard it —
 * exactly the case [DraftRegistration.flush] documents as its own absence.
 */
export function useDetailFormDraft(
  entity: string,
  id: MaybeRefOrGetter<string | undefined>,
  isDirty: () => boolean
): void {
  watch(
    () => toValue(id),
    (value, _old, onCleanup) => {
      if (!value) return
      onCleanup(registerDraft({ key: `detail-form:${entity}:${value}`, isDirty }))
    },
    { immediate: true }
  )
}

/** Whether any registered surface has unsaved work. */
export function anyDraftDirty(): boolean {
  for (const registration of registrations.values()) {
    if (registration.isDirty()) return true
  }
  return false
}

/**
 * Persist every dirty draft that can persist itself. Resolves `true` when the
 * whole registry is clean afterwards. Flush failures never throw — a surface
 * that cannot save stays dirty and the caller decides what that means.
 */
export async function flushAllDrafts(): Promise<boolean> {
  for (const registration of registrations.values()) {
    if (registration.isDirty() && registration.flush) {
      try {
        await registration.flush()
      } catch {
        // The surface shows its own save error; it simply stays dirty here.
      }
    }
  }
  return !anyDraftDirty()
}

/**
 * Make one surface clean before an in-page transition that would drop its
 * draft (e.g. collapsing a form). Flushes when possible, otherwise defers to
 * [confirmDiscard]. Resolves `true` when the transition may proceed.
 */
export async function ensureDraftClean(
  key: string,
  confirmDiscard: () => boolean
): Promise<boolean> {
  const registration = registrations.get(key)
  if (!registration || !registration.isDirty()) return true
  if (registration.flush) {
    try {
      if (await registration.flush()) return true
    } catch {
      // fall through to the confirm
    }
  }
  return confirmDiscard()
}

/** The standard question before a draft that cannot save itself is dropped. */
export function confirmDiscardDraft(): boolean {
  return window.confirm(getGlobalT()('dialogs.unsavedChanges.message'))
}

/** Test seam: forget every registration. */
export function resetDraftRegistry(): void {
  registrations.clear()
}

let guardInstalled = false

/**
 * Install the app-wide navigation guards over the registry. Called once from
 * App.vue; re-invocations are no-ops so hot reloads don't stack guards.
 *
 * Route changes first try to flush everything (autosaved surfaces leave
 * silently); only drafts that cannot or could not save ask for confirmation.
 * `beforeunload` cannot await, so it warns whenever anything is dirty.
 */
export function installDraftGuards(
  router: Router,
  confirmDiscard: () => Promise<boolean> | boolean
): void {
  if (guardInstalled) return
  guardInstalled = true

  router.beforeEach(async () => {
    if (!anyDraftDirty()) return true
    if (await flushAllDrafts()) return true
    return await confirmDiscard()
  })

  window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
    if (anyDraftDirty()) {
      e.preventDefault()
      e.returnValue = ''
    }
  })
}
